import { useGetStationsOnLangsteRoute } from "@/entities/langste-route";
import { TreinserieFormData, treinserieFormSchema } from "@/entities/opstartplan";
import { Station } from "@/entities/station";
import { Button, Form, FormErrorMessage, FormFieldInput } from "@/shared/components";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { cn } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { FieldPath, useForm, UseFormReturn } from "react-hook-form";
import { StationsAutocomplete } from "../infra";

type TreinserieFormProps = {
    defaultValues?: Partial<TreinserieFormData>;
    onCancel: () => void;
    onSubmit: (data: TreinserieFormData, serie?: number) => void;
};

export function TreinserieForm({ defaultValues, onCancel, onSubmit }: TreinserieFormProps) {
    const form = useForm<TreinserieFormData>({
        resolver: zodResolver(treinserieFormSchema),
        defaultValues,
    });

    const treinnummer = useDebouncedValue(form.watch("nummer"));
    const { data: treinserieMetTrajectStations } = useGetStationsOnLangsteRoute(treinnummer);

    const triggerValidation = form.trigger;
    const formState = form.formState;
    const errors = formState.errors;

    const mode = defaultValues ? "edit" : "add";

    const handleAdd = (data: TreinserieFormData) => {
        const serie = treinserieMetTrajectStations?.serie;
        if (!serie) {
            form.setError("root", { message: `Geen treinserie gevonden voor treinnummer ${treinnummer}` });
        } else {
            onSubmit(data, serie);
        }
    };

    const handleEdit = (data: TreinserieFormData) => {
        onSubmit(data);
    };

    const handleSubmit = (data: TreinserieFormData) => {
        const handler = mode === "add" ? handleAdd : handleEdit;
        handler(data);
    };

    useEffect(() => {
        // if editing an existing treinserie with invalid traject, trigger validation on mount to display error message
        if (defaultValues && defaultValues.traject && (!defaultValues.traject.vanStation || !defaultValues.traject.naarStation)) {
            void triggerValidation("traject");
        }
    }, [defaultValues, triggerValidation]);

    return (
        <Form {...form}>
            <form
                onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
                className="bg-emphasis-primary grid gap-4 rounded-xl p-4"
            >
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="grid grid-cols-2 items-start gap-2">
                        <FormFieldInput label="Treinnummer" form={form} name="nummer" placeholder="nr" readOnly={mode === "edit"} />
                        <FormFieldInput label="Vertrektijd" form={form} name="vertrekDatumTijd" type="time" />
                    </div>
                    <StationSelect
                        className="col-start-1 block"
                        form={form}
                        name="traject.vanStation"
                        preferredStations={treinserieMetTrajectStations?.stations}
                        placeholder="Van"
                    />
                    <StationSelect
                        className="col-start-2 block"
                        form={form}
                        name="traject.naarStation"
                        preferredStations={treinserieMetTrajectStations?.stations}
                        placeholder="Naar"
                    />
                </div>
                {errors.root && <FormErrorMessage>{errors.root.message}</FormErrorMessage>}
                <div className="flex justify-end gap-2">
                    <Button type="button" onClick={onCancel} variant="tertiary">
                        Annuleren
                    </Button>
                    <Button type="submit" disabled={formState.isSubmitting} variant="secondary">
                        Toevoegen
                    </Button>
                </div>
            </form>
        </Form>
    );
}

type StationSelectProps = {
    form: UseFormReturn<TreinserieFormData>;
    name: FieldPath<TreinserieFormData>;
    preferredStations?: Station[];
    placeholder: string;
    className?: string;
};

export function StationSelect({ form, name, preferredStations, placeholder, className }: Readonly<StationSelectProps>) {
    return (
        <div className={cn("w-max-[220px] text-content-default flex w-full min-w-0 items-center gap-4", className)}>
            <StationsAutocomplete
                data-testid={`stations-autocomplete-${name}`}
                form={form}
                name={name}
                showSearchIcon={false}
                placeholder={placeholder}
                label={null}
                preferredStations={preferredStations}
            />
        </div>
    );
}
